/** Tree visualization component for 3-level reseller hierarchy. */
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

/** Reseller node interface for tree structure. */
export interface ResellerNode {
  id: string;
  name: string;
  level: number;
  balance: number;
  children?: ResellerNode[];
  parentId?: string | null;
}

/** ResellerHierarchyTree component props. */
export interface ResellerHierarchyTreeProps {
  resellers: Array<{
    id: string;
    account: string;
    level: number | null;
    balance: number;
    parent_id?: string | null;
  }>;
  onResellerClick?: (resellerId: string) => void;
}

/** Build tree structure from flat reseller list. */
function buildTree(resellers: ResellerHierarchyTreeProps["resellers"]): ResellerNode[] {
  const nodeMap = new Map<string, ResellerNode>();
  const rootNodes: ResellerNode[] = [];

  // Create nodes
  resellers.forEach((reseller) => {
    const node: ResellerNode = {
      id: reseller.id,
      name: reseller.account,
      level: reseller.level || 1,
      balance: reseller.balance,
      children: [],
      parentId: reseller.parent_id,
    };
    nodeMap.set(reseller.id, node);
  });

  // Build hierarchy
  nodeMap.forEach((node) => {
    if (node.parentId && nodeMap.has(node.parentId)) {
      const parent = nodeMap.get(node.parentId);
      if (parent && parent.children) {
        parent.children.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  return rootNodes;
}

/** Get icon for reseller level. */
function getLevelIcon(level: number) {
  switch (level) {
    case 1:
      return <BusinessIcon fontSize="small" />;
    case 2:
      return <PeopleIcon fontSize="small" />;
    case 3:
      return <PersonIcon fontSize="small" />;
    default:
      return <BusinessIcon fontSize="small" />;
  }
}

/** Get level color. */
function getLevelColor(level: number): string {
  switch (level) {
    case 1:
      return "#1976d2"; // primary
    case 2:
      return "#dc004e"; // secondary
    case 3:
      return "#9c27b0"; // purple
    default:
      return "#757575"; // grey
  }
}

/** Filter tree nodes by search term. */
function filterTree(nodes: ResellerNode[], searchTerm: string): ResellerNode[] {
  if (!searchTerm.trim()) return nodes;

  const term = searchTerm.toLowerCase();

  const result: (ResellerNode | null)[] = nodes.map((node) => {
    // Check if current node matches
    const matches = node.name.toLowerCase().includes(term);

    // Filter children recursively
    const filteredChildren = filterTree(node.children || [], searchTerm);

    // Include node if it matches or has matching children
    if (matches || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren,
      };
    }

    return null;
  });

  return result.filter((node): node is ResellerNode => node !== null);
}

/** Tree node item component with expand/collapse. */
function TreeNode({
  node,
  level,
  selectedId,
  onNodeClick,
  isMobile,
}: {
  node: ResellerNode;
  level: number;
  selectedId: string | null;
  onNodeClick: (nodeId: string) => void;
  isMobile: boolean;
}) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;
  const levelColor = getLevelColor(node.level);

  const handleClick = () => {
    onNodeClick(node.id);
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: level * 1.5, // Indent based on depth
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <ListItemButton
          selected={isSelected}
          onClick={handleClick}
          sx={{
            py: 0.5,
            px: 1,
            minHeight: isMobile ? 44 : 40, // iOS touch target minimum
            backgroundColor: isSelected ? `${levelColor}15` : "transparent",
            "&:hover": {
              backgroundColor: isSelected ? `${levelColor}25` : "action.hover",
            },
            "&.Mui-selected": {
              backgroundColor: `${levelColor}20`,
              "&:hover": {
                backgroundColor: `${levelColor}30`,
              },
            },
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              sx={{ mr: 0.5, minWidth: 32, minHeight: 32 }}
             aria-label="List item">
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Box sx={{ color: levelColor }}>{getLevelIcon(node.level)}</Box>
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? "bold" : "normal",
                  color: isSelected ? levelColor : "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {node.name}
              </Typography>
            }
            secondary={
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
              >
                L{node.level} • ${node.balance.toFixed(2)}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              onNodeClick={onNodeClick}
              isMobile={isMobile}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

/** ResellerHierarchyTree component. */
export default function ResellerHierarchyTree({
  resellers,
  onResellerClick,
}: ResellerHierarchyTreeProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /** Build tree structure from resellers. */
  const treeData = useMemo(() => buildTree(resellers), [resellers]);

  /** Filter tree by search term. */
  const filteredTree = useMemo(
    () => filterTree(treeData, searchTerm),
    [treeData, searchTerm]
  );

  /** Handle node click. */
  const handleNodeClick = (nodeId: string) => {
    setSelectedId(nodeId);
    if (onResellerClick) {
      onResellerClick(nodeId);
    }
  };

  /** Main content. */
  const content = (
    <>
      {/* Search input with clear button */}
      <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search resellers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm("")}
                  sx={{ minWidth: 32, minHeight: 32 }}
                 aria-label="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiInputBase-input": {
              minHeight: isMobile ? 44 : 40, // iOS touch target
            },
          }}
        />
      </Box>

      {/* Tree view */}
      {filteredTree.length === 0 ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? "No matching resellers found" : "No resellers yet"}
          </Typography>
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            maxHeight: isTablet ? 400 : 500,
            overflowY: "auto",
          }}
        >
          <List disablePadding>
            {filteredTree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                selectedId={selectedId}
                onNodeClick={handleNodeClick}
                isMobile={isMobile}
              />
            ))}
          </List>
        </Paper>
      )}

      {/* Legend */}
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Level Legend
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <BusinessIcon fontSize="small" sx={{ color: "#1976d2" }} />
            <Typography variant="caption" color="text.secondary">
              Level 1
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PeopleIcon fontSize="small" sx={{ color: "#dc004e" }} />
            <Typography variant="caption" color="text.secondary">
              Level 2
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PersonIcon fontSize="small" sx={{ color: "#9c27b0" }} />
            <Typography variant="caption" color="text.secondary">
              Level 3
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );

  return content;
}
